'use client';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import logInstallPromptClicked from '@alltrails/amplitude/events/logInstallPromptClicked';
import Typography from '@alltrails/core/components/Typography';
import Button from '@alltrails/denali/components/Button';
import Heading from '@alltrails/denali/components/Heading';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import useDeeplink from '@alltrails/install-prompt/hooks/useDeeplink';
import styles from './AppInstallSection.module.scss';
import phoneMaskSrc from './images/phone-body.avif';
import QRCodeDownload from './QRCodeDownload';

type AppInstallData = {
  title: string;
  description: string;
  videoUrl: string;
};

const baseVideoUrl = `https://cdn-assets.alltrails.com/assets/videos/homepage-app-feature`;

const AppInstallSection = () => {
  const intl = useIntl();
  const getDeeplink = useDeeplink({});
  const isMobileSizedScreen = useIsMobileSizedScreen();

  const stackedContent: AppInstallData[] = [
    {
      title: intl.formatMessage({ defaultMessage: 'Record your activities' }),
      description: intl.formatMessage({ defaultMessage: 'Never miss a turn with live tracking. Relive every adventure with recaps and sharing.' }),
      videoUrl: `${baseVideoUrl}/navigator.mp4`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Explore even without service' }),
      description: intl.formatMessage({ defaultMessage: 'Download maps for trails and parks around the world.' }),
      videoUrl: `${baseVideoUrl}/offline.mp4`
    },
    {
      title: intl.formatMessage({ defaultMessage: 'Create your own route' }),
      description: intl.formatMessage({ defaultMessage: 'Build or edit the perfect trail for you.' }),
      videoUrl: `${baseVideoUrl}/byot.mp4`
    }
  ];

  const handleClick = () => {
    logInstallPromptClicked({
      install_prompt_location: 'home page',
      install_prompt_type: 'interstitial',
      install_prompt_source: 'native_with_appsflyer_link'
    });
    window.location.href = getDeeplink();
  };

  return (
    <section className={styles.outerContainer}>
      <div className={styles.container}>
        {stackedContent.map(({ title, description, videoUrl }, index) => {
          const alternateLayout = index === 1;

          return (
            <div key={index} className={classNames(styles.row, { [styles.rowReverse]: alternateLayout })}>
              <div className={classNames(styles.outerContent, { [styles.outerContentReverse]: alternateLayout })}>
                <div className={styles.content}>
                  <Heading as="h2" size="600">
                    {title}
                  </Heading>
                  <Typography variant="text300">{description}</Typography>
                  {!isMobileSizedScreen && <QRCodeDownload />}
                </div>
              </div>
              <div className={styles.phoneOuterContainer}>
                <div className={styles.phoneContainer}>
                  <img src={phoneMaskSrc.src} alt="" className={styles.phoneImg} />
                  <div className={styles.videoContainer}>
                    <video title={title} autoPlay loop muted playsInline className={styles.video}>
                      <source src={videoUrl} type="video/mp4" />
                    </video>
                  </div>
                </div>
                <div className={styles.bottomLine} />
              </div>
              {isMobileSizedScreen && (
                <Button
                  className={styles.button}
                  onClick={handleClick}
                  text={intl.formatMessage({ defaultMessage: 'Download the app' })}
                  variant="primary"
                />
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default AppInstallSection;
